import { useCallback, useEffect, useMemo, useState } from "react";
import {
  inviteCollaborators,
  listShares,
  updateSharePermission as updatePermissionRequest,
  removeShare as removeShareRequest,
  resendInvite as resendInviteRequest,
} from "../services/sharing.service";
import type {
  Share,
  SharePermission,
  ResourceType,
} from "../services/sharing.service";
import { toast } from "../lib/toast";

export interface UseSharingOptions {
  resourceType: ResourceType;
  resourceId: number;
  autoLoad?: boolean;
}

export function useSharing({ resourceType, resourceId, autoLoad = true }: UseSharingOptions) {
  const [shares, setShares] = useState<Share[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildShareKey = useCallback((share: Share) => {
    if (share.id !== null && share.id !== undefined) {
      return `id:${share.id}`;
    }

    const normalizedEmail = share.user_email.toLowerCase();
    return `implicit:${share.resource_type}:${share.resource_id}:${normalizedEmail}`;
  }, []);

  const loadShares = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listShares(resourceType, resourceId);
      setShares(data);
    } catch (err) {
      console.error(err);
      const errorMsg = err instanceof Error ? err.message : '';
      if (errorMsg.includes('conexão') || errorMsg.includes('internet') || errorMsg.includes('network')) {
        setError('Não foi possível carregar colaboradores — verifique sua conexão com a internet.');
      } else if (errorMsg.includes('permissão') || errorMsg.includes('403')) {
        setError('Você não tem permissão para ver os colaboradores deste recurso.');
      } else {
        setError('Não foi possível carregar a lista de colaboradores. Tente recarregar a página.');
      }
    } finally {
      setLoading(false);
    }
  }, [resourceType, resourceId]);

  useEffect(() => {
    if (autoLoad) {
      loadShares();
    }
  }, [autoLoad, loadShares]);

  const invite = useCallback(
    async (emails: string[], permission: SharePermission) => {
      try {
        const created = await inviteCollaborators({
          resourceType,
          resourceId,
          emails,
          permission,
        });
        toast.success("Convites enviados");
        setShares((prev) => {
          const map = new Map<string, Share>();
          prev.forEach((share) => map.set(buildShareKey(share), share));
          created.forEach((share) => map.set(buildShareKey(share), share));
          return Array.from(map.values());
        });
        return created;
      } catch (err: any) {
        console.error(err);
        const serverMsg = err?.response?.data?.message;
        const errMsg = err?.message || '';
        let message = 'Não foi possível enviar os convites. Verifique os e-mails e tente novamente.';
        if (serverMsg) {
          message = serverMsg;
        } else if (errMsg.includes('conexão') || errMsg.includes('internet')) {
          message = 'Não foi possível enviar convites — verifique sua conexão com a internet.';
        } else if (errMsg.includes('429') || errMsg.includes('rate')) {
          message = 'Muitas tentativas de convite. Aguarde alguns minutos e tente novamente.';
        }
        toast.error(message);
        throw err;
      }
    },
    [buildShareKey, resourceId, resourceType]
  );

  const updatePermission = useCallback(
    async (shareId: number, permission: SharePermission) => {
      const target = shares.find((share) => share.id === shareId);
      if (!target || target.origin !== "share") {
        toast.error("Não é possível alterar permissão deste colaborador");
        return;
      }

      try {
        const updated = await updatePermissionRequest(shareId, permission);
        if (updated) {
          updated.origin = "share";
          updated.isImplicitOwner = false;
        }
        setShares((prev) =>
          prev.map((share) => (share.id === shareId ? { ...share, ...updated } : share))
        );
        toast.success("Permissão atualizada");
      } catch (err: any) {
        console.error(err);
        const message =
          err?.response?.data?.message || 'Não foi possível atualizar a permissão do colaborador. Tente novamente.';
        toast.error(message);
        throw err;
      }
    },
    [shares]
  );

  const removeShare = useCallback(
    async (shareId: number | null) => {
      if (shareId === null || shareId === undefined) {
        toast.error("Não é possível remover colaborador implícito");
        return;
      }

      try {
        await removeShareRequest(shareId);
        setShares((prev) => prev.filter((share) => share.id !== shareId));
        toast.success("Colaborador removido");
      } catch (err: any) {
        console.error(err);
        const message = err?.response?.data?.message || 'Não foi possível remover o colaborador. Tente novamente.';
        toast.error(message);
        throw err;
      }
    },
    []
  );

  const resendInvite = useCallback(
    async (shareId: number | null) => {
      if (shareId === null || shareId === undefined) {
        toast.error("Convite não pode ser reenviado");
        return;
      }

      try {
        const updated = await resendInviteRequest(shareId);
        if (updated) {
          updated.origin = "share";
          updated.isImplicitOwner = false;
        }
        setShares((prev) =>
          prev.map((share) => (share.id === shareId ? { ...share, ...updated } : share))
        );
        toast.success("Convite reenviado");
      } catch (err: any) {
        console.error(err);
        const message = err?.response?.data?.message || 'Não foi possível reenviar o convite. Tente novamente em alguns minutos.';
        toast.error(message);
        throw err;
      }
    },
    [shares]
  );

  const acceptedCount = useMemo(
    () => shares.filter((share) => share.status === "accepted").length,
    [shares]
  );

  return {
    shares,
    loading,
    error,
    acceptedCount,
    reload: loadShares,
    invite,
    updatePermission,
    removeShare,
    resendInvite,
  };
}

